import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  X, ShoppingCart, Loader2, Lock, ArrowRight, Mail, Car, Building2,
  Minus, Plus, Users,
} from 'lucide-react';
import { createSubscriptionCheckout } from '../services/api';
import { toast } from 'sonner';
import PaymentGrid from './PaymentLogos';
import { TOP_USPS_SHORT, CONCEPT_USPS } from '../data/topUsps';
import { useCart } from '../context/CartContext';
import KlarnaBnplBadge from './KlarnaBnplBadge';
import ProductImageSlider from './ProductImageSlider';
import CheckoutReviewBadge from './CheckoutReviewBadge';
// import LoyaltyTiers from './LoyaltyTiers'; // aus Drawer entfernt (User-Wunsch)
import MiniReviews from './MiniReviews';
import { FleetInquiryForm } from './configurator/FleetInquiryForm';

import { RangeSliderField } from './shared/RangeSliderField';
import { EmbeddedCardBenefits } from './landing/CardBenefitsShowcase';
import { calcPrivate, GOLD_CARD_PRICE } from '../lib/calculator';
// import AnnualFuelCostCard from './shared/AnnualFuelCostCard'; // aus Drawer entfernt (User-Wunsch)
import ConversionUSPBar from './ConversionUSPBar';
/**
 * CheckoutDrawer v4 — heller, minimalistischer Slide-in mit Inline-Konfigurator.
 * Synced mit dem Homepage-Configurator via onConfigChange.
 */
export default function CheckoutDrawer({ open, onClose, config: initialConfig, onConfigChange, inline = false }) {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [mode, setMode] = useState(initialConfig?.mode || 'private');
  const [vehicles, setVehicles] = useState(initialConfig?.vehicles || 1);
  const [annualKm, setAnnualKm] = useState(initialConfig?.annualKm || 15000);
  const [employees, setEmployees] = useState(initialConfig?.employees || 50);
  const [householdAdults, setHouseholdAdults] = useState(initialConfig?.householdAdults || 1);
  const [householdVehicles, setHouseholdVehicles] = useState(initialConfig?.householdVehicles || 1);

  // Loading-State beim Öffnen IMMER zurücksetzen — verhindert stuck "Weiterleitung..."
  // wenn der Kunde nach dem Klick zurücknavigiert und den Drawer erneut öffnet.
  useEffect(() => { if (open) setLoading(false); }, [open]);

  useEffect(() => {
    if (open && initialConfig) {
      setMode(initialConfig.mode ?? 'private');
      setVehicles(initialConfig.vehicles ?? 1);
      setAnnualKm(initialConfig.annualKm ?? 15000);
      setEmployees(initialConfig.employees ?? 50);
      setHouseholdAdults(initialConfig.householdAdults ?? 1);
      setHouseholdVehicles(initialConfig.householdVehicles ?? 1);
    }
  }, [open, initialConfig]);

  // Debounced upstream sync: sonst re-rendert das Parent bei jedem Slider-Tick
  useEffect(() => {
    if (!open || !onConfigChange) return undefined;
    const handle = setTimeout(() => {
      onConfigChange({ mode, vehicles, annualKm, employees, householdAdults, householdVehicles });
    }, 200);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, vehicles, annualKm, employees, householdAdults, householdVehicles, open]);

  useEffect(() => {
    if (!open || inline) return undefined;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [open, inline]);

  // Performance: Checkout-Chunk + Stripe.js schon beim Öffnen des Drawers vorladen —
  // die Zahlungsseite erscheint dann ohne Ladeverzögerung
  useEffect(() => {
    if (!open) return;
    import('../pages/CheckoutPage');
    ['https://js.stripe.com', 'https://m.stripe.network'].forEach((href) => {
      if (document.querySelector(`link[rel="preconnect"][href="${href}"]`)) return;
      const l = document.createElement('link');
      l.rel = 'preconnect';
      l.href = href;
      document.head.appendChild(l);
    });
  }, [open]);

  if (!open || !initialConfig) return null;

  const isPvt = mode === 'private';
  const cardName = isPvt ? 'Silver Card' : 'Gold Card';

  const needsFleetInquiry = !isPvt && vehicles > 200;

  // Spiegel der Backend-Kalkulation — Private nutzt zentralen Calculator (Staffelpreise · einheitliche Rate 0,075 €/km)
  const estFee = isPvt
    ? calcPrivate({ vehicles: 1, annual_km: annualKm }).card_fee
    : Math.round(vehicles * GOLD_CARD_PRICE * 100) / 100;

  const fmt = (v, d = 2) => v.toLocaleString('de-DE', { minimumFractionDigits: d });

  const handleCheckout = async () => {
    setLoading(true);
    // InitiateCheckout / begin_checkout wird zentral auf der Checkout-Seite gefeuert
    // (einmal pro Session, deckt auch Direkt-Einstiege via Ad-Deeplink ab).
    // Direkt in den Express-Checkout auf /checkout?express=1
    const isPvtLocal = mode === 'private';
    const params = new URLSearchParams({
      express: '1',
      package: isPvtLocal ? 'silver' : 'gold',
      km: String(annualKm || 15000),
      vehicles: String(vehicles || 1),
    });
    onClose?.();
    navigate(`/checkout?${params.toString()}`);
  };

  const panel = (
    <div
      className={inline
        ? "relative w-full max-w-md mx-auto bg-white sm:border sm:border-stone-200 sm:rounded-2xl text-stone-900 overflow-hidden"
        : "relative w-full max-w-md bg-white border-l border-stone-200 text-stone-900 h-full overflow-y-auto overscroll-contain shadow-2xl animate-in slide-in-from-right duration-300"}
      onClick={inline ? undefined : (e) => e.stopPropagation()}
    >
        {/* Header — großes Brand-Logo + Sicherheits-Signal */}
        <div className="sticky top-0 bg-white/95 backdrop-blur-xl border-b border-stone-200 px-6 py-5 flex items-center justify-between z-30">
          <div className="flex items-center gap-3.5">
            {inline ? (
              <Link to="/" aria-label="Zur Startseite" data-testid="landing-logo-home">
                <img
                  src="/fuelx-logo-final-cropped-96.webp"
                  alt="FUEL.X"
                  className="h-6 w-auto"
                  data-testid="checkout-drawer-logo"
                />
              </Link>
            ) : (
              <img
                src="/fuelx-logo-final-cropped-96.webp"
                alt="FUEL.X"
                className="h-6 w-auto"
                data-testid="checkout-drawer-logo"
              />
            )}
            <span className="h-5 w-px bg-stone-200" />
            <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-emerald-600 flex items-center gap-1.5">
              <Lock className="w-3 h-3" strokeWidth={2.5} /> Sicherer Checkout
            </p>
          </div>
          {inline ? (
            <Link
              to="/"
              aria-label="Schließen — zur Startseite"
              className="w-9 h-9 rounded-full hover:bg-stone-100 flex items-center justify-center text-stone-500 hover:text-stone-900 transition-colors"
              data-testid="landing-close-home"
            >
              <X className="w-4 h-4" strokeWidth={2} />
            </Link>
          ) : (
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-full hover:bg-stone-100 flex items-center justify-center text-stone-500 hover:text-stone-900 transition-colors"
              data-testid="checkout-close"
            >
              <X className="w-4 h-4" strokeWidth={2} />
            </button>
          )}
        </div>

        <div className="px-6 py-6 space-y-6">
          {/* Card Preview — dunkler Showcase mit Produktbilder-Slider */}
          <div className="rounded-2xl overflow-hidden bg-stone-950 -mx-3 sm:mx-0" data-testid="checkout-summary">
            <ProductImageSlider
              compact
              business={!isPvt}
              testIdPrefix="drawer-product-slider"
              onNavigate={onClose}
              cardSrc={isPvt ? '/silver-card-front.webp' : '/gold-card-front.webp'}
              cardAlt={cardName}
              secondSrc={null}
              secondAlt={`${cardName} · durchschnittlich 0,40 €/Liter · Benzin, Diesel & Elektro`}
              firstSlide={
                <div className="w-full h-full flex items-center justify-center px-2">
                  <img
                    src={isPvt ? '/silver-card-front.webp' : '/gold-card-front.webp'}
                    alt={cardName}
                    className="w-full object-contain drop-shadow-[0_20px_35px_rgba(0,0,0,0.6)]"
                  />
                </div>
              }
            />
            <div className="px-5 pb-5 pt-2 flex items-end justify-between">
              <div>
                <p className="text-[10px] font-medium uppercase tracking-[0.2em] text-stone-500">
                  {isPvt ? 'Privat' : 'Business'}
                </p>
                <h4 className="text-sm font-medium text-white mt-0.5">{cardName}</h4>
                <p className="text-[11px] text-stone-500 mt-0.5">Jahresmitgliedschaft · {annualKm.toLocaleString('de-DE')} km/Jahr</p>
              </div>
              <p className="text-base font-medium text-white tabular-nums">
                {fmt(estFee)}<span className="text-xs text-stone-500 ml-1">EUR</span>
              </p>
            </div>
          </div>

          {/* Variante */}
          <div data-testid="drawer-variant-switcher">
            <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-stone-500 mb-2">Variante</p>
            <div className="grid grid-cols-2 gap-1 p-1 bg-stone-100 rounded-full">
              {[
                ['private', 'Silver · Privat', <Car key="p" className="w-3.5 h-3.5" strokeWidth={2} />],
                ['business', 'Gold · Business', <Building2 key="b" className="w-3.5 h-3.5" strokeWidth={2} />],
              ].map(([m, l, icon]) => (
                <button
                  key={m}
                  onClick={() => {
                    setMode(m);
                    if (m === 'private') { setVehicles(1); setAnnualKm(15000); }
                    else { setVehicles(5); setAnnualKm(25000); setEmployees(50); }
                  }}
                  className={`flex items-center justify-center gap-1.5 py-2.5 rounded-full text-xs font-medium transition-colors ${
                    mode === m ? 'bg-stone-900 text-white' : 'text-stone-500 hover:text-stone-900'
                  }`}
                  data-testid={`drawer-variant-${m}`}
                >
                  {icon}{l}
                </button>
              ))}
            </div>
          </div>

          {/* Haushalt (Privat) */}
          {isPvt && (
            <div data-testid="drawer-household">
              <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-stone-500 mb-2">Dein Haushalt</p>
              <div className="grid grid-cols-2 gap-2.5">
                {[
                  ['Personen (ab 18 J.)', householdAdults, setHouseholdAdults, 'drawer-adults'],
                  ['Fahrzeuge', householdVehicles, setHouseholdVehicles, 'drawer-hh-vehicles'],
                ].map(([label, val, setter, tid]) => (
                  <div key={tid}>
                    <label className="text-[10px] text-stone-500 block mb-1">{label}</label>
                    <div className="flex items-center justify-between rounded-full border border-stone-200 px-1 py-0.5">
                      <button type="button" onClick={() => setter(Math.max(1, val - 1))} className="w-7 h-7 rounded-full flex items-center justify-center text-stone-500 hover:text-stone-900 hover:bg-stone-100 transition-colors" data-testid={`${tid}-minus`}>
                        <Minus className="w-3 h-3" strokeWidth={2} />
                      </button>
                      <span className="text-sm font-medium text-stone-900 tabular-nums" data-testid={`${tid}-value`}>{val}</span>
                      <button type="button" onClick={() => setter(Math.min(4, val + 1))} className="w-7 h-7 rounded-full flex items-center justify-center text-stone-500 hover:text-stone-900 hover:bg-stone-100 transition-colors" data-testid={`${tid}-plus`}>
                        <Plus className="w-3 h-3" strokeWidth={2} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="rounded-xl bg-gradient-to-r from-emerald-50 via-teal-50/70 to-emerald-50/40 border border-emerald-200/80 shadow-[inset_0_1px_0_rgba(255,255,255,0.85)] px-3.5 py-3 mt-2.5 flex items-start gap-2.5" data-testid="drawer-household-info">
                <span className="w-7 h-7 rounded-full bg-white border border-emerald-200 flex items-center justify-center flex-shrink-0 shadow-sm">
                  <Users className="w-3.5 h-3.5 text-emerald-600" strokeWidth={2} />
                </span>
                <p className="text-[11px] leading-relaxed text-emerald-900">
                  <strong className="font-semibold">Bis zu 4 Familienmitglieder im selben Haushalt inklusive</strong> — gleicher Preis, gleiches Guthaben. Mitglieder & Fahrzeuge fügst du nach dem Kauf im Account hinzu.
                </p>
              </div>
            </div>
          )}

          {/* Fahrzeuge (Business) */}
          {!isPvt && (
            <RangeSliderField
              label="Fahrzeuge"
              value={Math.min(vehicles, 201)}
              min={1} max={201} step={1} stepBy={1}
              displayValue={vehicles > 200 ? '201+' : vehicles.toLocaleString('de-DE')}
              onValueChange={setVehicles}
              minLabel="1" maxLabel="201+"
              testId="drawer-veh"
            />
          )}

          {/* Mitarbeiter (Business) */}
          {!isPvt && (
            <RangeSliderField
              label="Mitarbeiter"
              hint={`min. ${vehicles}`}
              value={Math.min(Math.max(employees, vehicles), 2000)}
              min={vehicles} max={2000} step={1} stepBy={10}
              onValueChange={setEmployees}
              minLabel={vehicles.toLocaleString('de-DE')} maxLabel="2.000+"
              testId="drawer-emp"
            />
          )}

          {/* km */}
          <RangeSliderField
            label={isPvt ? 'Kilometer / Jahr' : 'km / Fzg / Jahr'}
            value={annualKm}
            min={5000} max={isPvt ? 40000 : 50000} step={1000} stepBy={1000}
            unit="km"
            onValueChange={setAnnualKm}
            minLabel="5.000" maxLabel={isPvt ? '40.000' : '50.000'}
            testId="drawer-km"
          />

          {/* Leistungen — Privat & Business: „Für mehr Auto"-Karten (dunkelgrün, mit Bildern, scrollbar) */}
          <div className="-mx-6 px-6" data-testid="drawer-fuelx-benefits">
            <EmbeddedCardBenefits testId="drawer-benefits" />
          </div>

          {/* Kundenstimmen — endloser Slider nach links als Social Proof */}
          <div className="text-left -mx-6 px-6" data-testid="drawer-reviews-wrap">
            <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-emerald-700 mb-3 px-0">Echte Kunden · Echte Ersparnis</p>
            <MiniReviews variant="marquee" limit={8} context="cart" cartVariant={isPvt ? 'private' : 'business'} testIdPrefix="drawer-reviews" />
          </div>

          {/* Total + CTA — direkt nach den Vorteilen: weniger Scroll bis zum Kauf */}
          <div className="border-t border-stone-200 pt-5">
            {/* Value-Anker */}
            <div className="relative overflow-hidden rounded-2xl bg-emerald-950 p-4 mb-4" data-testid="drawer-value-anchor">
              <div aria-hidden="true" className="absolute -top-12 -right-10 w-40 h-40 rounded-full bg-emerald-400/10 blur-2xl" />
              <p className="relative text-[9px] uppercase tracking-[0.16em] text-emerald-50/50">Gesamt · inkl. MwSt.</p>
              <p className="relative text-3xl font-display tracking-tight text-white tabular-nums mt-1" data-testid="cart-total-eur">
                {fmt(estFee)}<span className="text-sm text-emerald-50/50 ml-1.5">EUR</span>
              </p>
            </div>

            {needsFleetInquiry ? (
              <div data-testid="drawer-fleet-inquiry">
                <FleetInquiryForm light vehicles={vehicles} annualKm={annualKm} />
              </div>
            ) : (
              <>
                {inline && (
                  <div className="mb-3 rounded-2xl border border-stone-200 bg-stone-50/80 px-4 py-3" data-testid="landing-testimonial">
                    <CheckoutReviewBadge variant={isPvt ? 'private' : 'business'} testId="landing-review" />
                  </div>
                )}
                {/* Trust-Pills direkt über CTA — reduziert Klick-Reibung */}
                <div className="mb-3" data-testid="drawer-trust-pills-wrap">
                  <ConversionUSPBar variant="pills" testId="drawer-trust-pills" />
                </div>
                <button
                  onClick={handleCheckout}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-emerald-400 text-emerald-950 text-sm font-semibold hover:bg-emerald-300 shadow-neon-sm hover:shadow-neon transition duration-200 hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
                  data-testid="checkout-pay-btn"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} /> Weiterleitung ...</>
                  ) : (
                    <><Lock className="w-4 h-4" strokeWidth={2} /> Zur sicheren Kasse <ArrowRight className="w-4 h-4" strokeWidth={2} /></>
                  )}
                </button>
              </>
            )}

            <Link
              to="/warenkorb"
              onClick={onClose}
              className="w-full flex items-center justify-center gap-2 py-3 mt-3 rounded-full border border-stone-300 bg-stone-50 text-sm font-medium text-stone-700 hover:bg-stone-100 hover:border-stone-400 hover:text-stone-900 transition-colors"
              data-testid="checkout-drawer-warenkorb-link"
            >
              <ShoppingCart className="w-4 h-4" strokeWidth={2} /> Warenkorb in Vollansicht öffnen
            </Link>

            {/* Zahlungsmethoden — kompakt, unter der Vollansicht */}
            <div className="mt-4" data-testid="checkout-payment-methods">
              <PaymentGrid cols={5} small />
              <KlarnaBnplBadge testId="drawer-klarna-bnpl" compact />
            </div>

            {/* Value-Stack — was in der Card steckt */}
            <div className="mt-4">
              <ConversionUSPBar variant="valuestack" compact testId="drawer-value-stack" />
            </div>
          </div>
        </div>
      </div>
  );

  if (inline) return panel;

  return (
    <div
      className="fixed inset-0 z-[100] flex justify-end"
      role="dialog"
      aria-modal="true"
      aria-label="Warenkorb & Checkout"
      data-testid="checkout-drawer"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-stone-950/30 backdrop-blur-sm" />
      {panel}
    </div>
  );
}
